import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'

// Document UIDs are UUIDs (see internal/models — `uid uuid.UUID`).
const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const UUID_RE = new RegExp(`^${UUID_PATTERN}$`, 'i')
const DOCUMENT_PATH_RE = new RegExp(`/documents/(${UUID_PATTERN})(?:[/?#]|$)`, 'i')

/**
 * Extracts a document UID from a pasted Buildtory document URL
 * (matching this app's own `/{org}/documents/{documentUid}` route — see
 * `front/src/routes/$org/documents/$docUid.tsx`) or a bare UID string typed
 * or pasted directly. Returns `null` when `input` doesn't look like either,
 * so callers fall back to their normal substring search.
 *
 * Out of scope by design: URLs to documents outside this app.
 */
export function extractDocumentUid(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  const pathMatch = trimmed.match(DOCUMENT_PATH_RE)
  if (pathMatch) return pathMatch[1].toLowerCase()
  if (UUID_RE.test(trimmed)) return trimmed.toLowerCase()
  return null
}

export interface ResolvedPickerDocument {
  uid: string
  name: string
  thumbnailUrl: string
  /** The resolved audience (`internal` | `shared`), when the API sent it. */
  effective_visibility?: string
}

/**
 * Shared "paste a document URL/UID into a picker search box" resolver, used
 * by every attach-existing-document picker (intervention documents, asset
 * compliance evidence, …) so they don't each re-implement URL parsing.
 *
 * When `search` looks like a document URL or bare UID, resolves it via
 * `GET /organizations/{org}/documents/{documentUid}` and returns it as a
 * single pinned result (with a thumbnail URL for the row to render); the
 * caller should skip its normal name-substring filter while
 * `isPastedReference` is true and show its existing "not found" empty state
 * when `document` comes back `null` after loading.
 */
export function useDocumentUrlResolver(org: string, search: string, enabled: boolean) {
  const uid = useMemo(() => extractDocumentUid(search), [search])

  const query = useQuery({
    queryKey: ['document-url-resolver', org, uid],
    queryFn: async (): Promise<ResolvedPickerDocument | null> => {
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBaseUrl}/api/v1/organizations/${org}/documents/${uid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) return null // not found, wrong org, etc. — handled as "no match" by the caller
      const doc = await res.json()
      const docUid = doc?.uid || uid
      return {
        uid: docUid,
        name: doc?.name || doc?.file_name || docUid,
        thumbnailUrl: `${apiBaseUrl}/api/v1/organizations/${org}/documents/${docUid}/thumb?size=64`,
        effective_visibility: doc?.effective_visibility,
      }
    },
    enabled: enabled && !!org && !!uid,
    staleTime: 60_000,
    retry: false,
  })

  return {
    /** True while `search` parses as a document URL/UID — caller should show this hook's result instead of its normal filter. */
    isPastedReference: uid != null,
    /** The resolved document, or `null` once loaded with no match (not found / different org). `undefined` while loading. */
    document: query.data,
    isLoading: uid != null && query.isFetching,
  }
}
